<template>
  <div>
    <div
      v-if="submitError"
      class="alert alert-danger alert-dismissible"
      role="alert"
    >
      <button
        type="button"
        class="close"
        @click="submitError = null"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <strong>Error:</strong> {{ submitError }}
    </div>
    <div v-if="verifyReference" class="alert alert-success" role="alert">
      <i class="fa fa-check-circle mr-2"></i>
      <strong>Submitted to Verify</strong> &mdash; Reference:
      <span class="badge badge-success">{{ verifyReference }}</span>
      <span v-if="verifyIncidentId" class="ml-2 text-muted">
        (Incident ID: {{ verifyIncidentId }})
      </span>
    </div>
    <form class="form-horizontal" @submit.prevent="submitToVerify">
      <div class="card mb-3">
        <div class="card-body">
          <fieldset>
            <legend>Claimant</legend>
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('claimant_vrn') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">VRN</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claimant_vrn) }}
                    </p>
                    <p
                      v-if="hasError('claimant_vrn')"
                      class="help-block text-danger"
                      v-text="getError('claimant_vrn')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('claimant_name') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Name</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claimant_name) }}
                    </p>
                    <p
                      v-if="hasError('claimant_name')"
                      class="help-block text-danger"
                      v-text="getError('claimant_name')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('claimant_address') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Address</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claimant_address) }}
                    </p>
                    <p
                      v-if="hasError('claimant_address')"
                      class="help-block text-danger"
                      v-text="getError('claimant_address')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('claimant_postcode') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Postcode</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claimant_postcode) }}
                    </p>
                    <p
                      v-if="hasError('claimant_postcode')"
                      class="help-block text-danger"
                      v-text="getError('claimant_postcode')"
                    ></p>
                  </div>
                </div>
              </div>

              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('claimant_insurer_claim_reference')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claims Reference</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claim_reference) }}
                    </p>
                    <p
                      v-if="hasError('claim_reference')"
                      class="help-block text-danger"
                      v-text="getError('claim_reference')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('claimant_insurer_id') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label"
                      >Insurer Organisation Name</label
                    >
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatValue(getInsurerName(form.claimant_insurer_id))
                      }}
                    </p>
                    <p
                      v-if="hasError('claimant_insurer_id')"
                      class="help-block text-danger"
                      v-text="getError('claimant_insurer_id')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('claimant_insurer_claim_reference')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label"
                      >Insurer Claims Reference</label
                    >
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claimant_insurer_claim_reference) }}
                    </p>
                    <p
                      v-if="hasError('claimant_insurer_claim_reference')"
                      class="help-block text-danger"
                      v-text="getError('claimant_insurer_claim_reference')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('claimant_insurer_policy_number')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Insurer Policy Number</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.claimant_insurer_policy_number) }}
                    </p>
                    <p
                      v-if="hasError('claimant_insurer_policy_number')"
                      class="help-block text-danger"
                      v-text="getError('claimant_insurer_policy_number')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('claimant_insurance_cover_id')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Insurance Cover</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatValue(
                          getCoverLevelName(form.claimant_insurance_cover_id)
                        )
                      }}
                    </p>
                    <p
                      v-if="hasError('claimant_insurance_cover_id')"
                      class="help-block text-danger"
                      v-text="getError('claimant_insurance_cover_id')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('claimant_vat_registered') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">VAT Registered?</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatBoolean(form.claimant_vat_registered) }}
                    </p>
                    <p
                      v-if="hasError('claimant_vat_registered')"
                      class="help-block text-danger"
                      v-text="getError('claimant_vat_registered')"
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Defendant</legend>
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('defendant_insurer_id') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Insurer Name</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatValue(getInsurerName(form.defendant_insurer_id))
                      }}
                    </p>
                    <p
                      v-if="hasError('defendant_insurer_id')"
                      class="help-block text-danger"
                      v-text="getError('defendant_insurer_id')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('defendant_insurer_policy_number')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Insurer Policy Number</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.defendant_insurer_policy_number) }}
                    </p>
                    <p
                      v-if="hasError('defendant_insurer_policy_number')"
                      class="help-block text-danger"
                      v-text="getError('defendant_insurer_policy_number')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('defendant_insurer_ref') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Insurer Ref</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.defendant_insurer_ref) }}
                    </p>
                    <p
                      v-if="hasError('defendant_insurer_ref')"
                      class="help-block text-danger"
                      v-text="getError('defendant_insurer_ref')"
                    ></p>
                  </div>
                </div>
              </div>

              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('defendant_vrn') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">VRN</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.defendant_vrn) }}
                    </p>
                    <p
                      v-if="hasError('defendant_vrn')"
                      class="help-block text-danger"
                      v-text="getError('defendant_vrn')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('defendant_name') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Name</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.defendant_name) }}
                    </p>
                    <p
                      v-if="hasError('defendant_name')"
                      class="help-block text-danger"
                      v-text="getError('defendant_name')"
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Incident</legend>
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('incident_date') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Incident Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.incident_date) }}
                    </p>
                    <p
                      v-if="hasError('incident_date')"
                      class="help-block text-danger"
                      v-text="getError('incident_date')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('accident_location') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Accident Location</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.accident_location) }}
                    </p>
                    <p
                      v-if="hasError('accident_location')"
                      class="help-block text-danger"
                      v-text="getError('accident_location')"
                    ></p>
                  </div>
                </div>

                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('garage_details') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Garage Details</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.garage_details) }}
                    </p>
                    <p
                      v-if="hasError('garage_details')"
                      class="help-block text-danger"
                      v-text="getError('garage_details')"
                    ></p>
                  </div>
                </div>
              </div>

              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('accident_circumstances') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Accident Circumstances</label>
                  </div>
                  <div class="col-lg-8">
                    <textarea
                      v-model="form.accident_circumstances"
                      class="form-control"
                      rows="4"
                      :maxlength="maxCircumstancesLength"
                      style="resize: vertical;"
                      @input="handleCircumstancesLimit"
                    ></textarea>
                    <small class="text-muted">
                      {{ (form.accident_circumstances || '').length }} /
                      {{ maxCircumstancesLength }} characters
                    </small>
                    <div v-if="isCircumstancesEdited" style="margin-top: 2px;">
                      <small class="text-muted" style="font-style: italic;">
                        <i
                          class="material-icons icon"
                          style="font-size: 12px; vertical-align: middle;"
                          >info</i
                        >
                        Using edited version (original description preserved)
                      </small>
                    </div>
                    <p
                      v-if="hasError('accident_circumstances')"
                      class="help-block text-danger"
                      v-text="getError('accident_circumstances')"
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Other details</legend>
            <div class="row">
              <div class="col-lg-6">
                <selectlist
                  name="damage_claimed"
                  placeholder="Please Select"
                  label="Damage Claimed"
                  v-model="form.damage_claimed"
                  :options="damageClaimedOptions"
                  key-name="value"
                  label-name="label"
                  :show-error="hasError('damage_claimed')"
                  :error-message="getError('damage_claimed')"
                ></selectlist>

                <selectlist
                  name="vehicle_condition"
                  placeholder="Please Select"
                  label="Vehicle Condition"
                  v-model="form.vehicle_condition"
                  :options="vehicleConditionOptions"
                  key-name="value"
                  label-name="label"
                  :show-error="hasError('vehicle_condition')"
                  :error-message="getError('vehicle_condition')"
                ></selectlist>
              </div>

              <div class="col-lg-6">
                <selectlist
                  name="inspection_requested_by"
                  placeholder="Please Select"
                  label="Inspection Request By"
                  v-model="form.inspection_requested_by"
                  :options="partyOptions"
                  key-name="value"
                  label-name="label"
                  :show-error="hasError('inspection_requested_by')"
                  :error-message="getError('inspection_requested_by')"
                ></selectlist>

                <selectlist
                  name="party_dealing"
                  placeholder="Please Select"
                  label="Party Dealing"
                  v-model="form.party_dealing"
                  :options="partyOptions"
                  key-name="value"
                  label-name="label"
                  :show-error="hasError('party_dealing')"
                  :error-message="getError('party_dealing')"
                ></selectlist>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Loss Of Use Dates Dates</legend>
            <div class="row">
              <div
                class="col-lg-6"
                v-for="field in dateFields"
                v-bind:key="field.key"
              >
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError(field.key) }"
                >
                  <div class="col-lg-4">
                    <label :for="field.key" class="control-label">{{
                      field.label
                    }}</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form[field.key]) }}
                    </p>
                    <p
                      v-if="hasError(field.key)"
                      class="help-block text-danger"
                      v-text="getError(field.key)"
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </div>

      <h4 class="mb-3">Figures</h4>
      <div v-if="figuresLoading" class="text-muted">Loading figures...</div>
      <div v-else-if="figuresError" class="text-danger">{{ figuresError }}</div>
      <div v-else-if="form.figures">
        <div class="card mb-3">
          <div class="card-header">Hire</div>
          <div class="card-body">
            <p class="text-muted" v-if="!form.figures.hire.items.length">
              No hire figures available.
            </p>
            <div v-else>
              <div
                class="card mb-3"
                v-for="(hireItem, index) in form.figures.hire.items"
                :key="index"
              >
                <div class="card-header">Hire {{ index + 1 }}</div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireItemErrorKey(index, 'start_date')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Start Date</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatValue(hireItem.start_date) }}
                          </p>
                          <p
                            v-if="
                              hasError(hireItemErrorKey(index, 'start_date'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireItemErrorKey(index, 'start_date'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireItemErrorKey(index, 'end_date')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">End Date</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatValue(hireItem.end_date) }}
                          </p>
                          <p
                            v-if="hasError(hireItemErrorKey(index, 'end_date'))"
                            class="help-block text-danger"
                            v-text="
                              getError(hireItemErrorKey(index, 'end_date'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(hireItemErrorKey(index, 'vrn'))
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">VRN</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatValue(hireItem.vrn) }}
                          </p>
                          <p
                            v-if="hasError(hireItemErrorKey(index, 'vrn'))"
                            class="help-block text-danger"
                            v-text="getError(hireItemErrorKey(index, 'vrn'))"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireItemErrorKey(index, 'days_claimed')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Days Claimed</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatValue(hireItem.days_claimed) }}
                          </p>
                          <p
                            v-if="
                              hasError(hireItemErrorKey(index, 'days_claimed'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireItemErrorKey(index, 'days_claimed'))
                            "
                          ></p>
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireItemErrorKey(index, 'daily_rate')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Daily Rate</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(hireItem.daily_rate) }}
                          </p>
                          <p
                            v-if="
                              hasError(hireItemErrorKey(index, 'daily_rate'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireItemErrorKey(index, 'daily_rate'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'automatic')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra Automatic</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(hireItem.daily_extras.automatic)
                            }}
                          </p>
                          <p
                            v-if="
                              hasError(hireExtraErrorKey(index, 'automatic'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'automatic'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'car_seat')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra Car Seat</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(hireItem.daily_extras.car_seat) }}
                          </p>
                          <p
                            v-if="
                              hasError(hireExtraErrorKey(index, 'car_seat'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'car_seat'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'dual_control')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label"
                            >Extra Dual Control</label
                          >
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(hireItem.daily_extras.dual_control)
                            }}
                          </p>
                          <p
                            v-if="
                              hasError(hireExtraErrorKey(index, 'dual_control'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'dual_control'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'estate')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra Estate</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(hireItem.daily_extras.estate) }}
                          </p>
                          <p
                            v-if="hasError(hireExtraErrorKey(index, 'estate'))"
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'estate'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'roof_rack')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra Roof Rack</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(hireItem.daily_extras.roof_rack)
                            }}
                          </p>
                          <p
                            v-if="
                              hasError(hireExtraErrorKey(index, 'roof_rack'))
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'roof_rack'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'sat_nav')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra Sat Nav</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(hireItem.daily_extras.sat_nav) }}
                          </p>
                          <p
                            v-if="hasError(hireExtraErrorKey(index, 'sat_nav'))"
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'sat_nav'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'tow_bar')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra Tow Bar</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(hireItem.daily_extras.tow_bar) }}
                          </p>
                          <p
                            v-if="hasError(hireExtraErrorKey(index, 'tow_bar'))"
                            class="help-block text-danger"
                            v-text="
                              getError(hireExtraErrorKey(index, 'tow_bar'))
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(hireExtraErrorKey(index, 'cdw'))
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Extra CDW</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(hireItem.daily_extras.cdw) }}
                          </p>
                          <p
                            v-if="hasError(hireExtraErrorKey(index, 'cdw'))"
                            class="help-block text-danger"
                            v-text="getError(hireExtraErrorKey(index, 'cdw'))"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            hireExtraErrorKey(index, 'additional_driver')
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label"
                            >Extra Additional Driver</label
                          >
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(
                                hireItem.daily_extras.additional_driver
                              )
                            }}
                          </p>
                          <p
                            v-if="
                              hasError(
                                hireExtraErrorKey(index, 'additional_driver')
                              )
                            "
                            class="help-block text-danger"
                            v-text="
                              getError(
                                hireExtraErrorKey(index, 'additional_driver')
                              )
                            "
                          ></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-lg-6">
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.non_standard_risk_reason'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >Non-Standard Risk Driver Reason</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatRiskReason(
                              form.figures.hire.non_standard_risk_reason
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError('figures.hire.non_standard_risk_reason')
                          "
                          class="help-block text-danger"
                          v-text="
                            getError('figures.hire.non_standard_risk_reason')
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.invoice_date'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Invoice Date</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatValue(form.figures.hire.totals.invoice_date)
                          }}
                        </p>
                        <p
                          v-if="hasError('figures.hire.totals.invoice_date')"
                          class="help-block text-danger"
                          v-text="getError('figures.hire.totals.invoice_date')"
                        ></p>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.delivery_collection.amount'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >Delivery & Collection Amount</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.delivery_collection
                                .amount
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError(
                              'figures.hire.totals.delivery_collection.amount'
                            )
                          "
                          class="help-block text-danger"
                          v-text="
                            getError(
                              'figures.hire.totals.delivery_collection.amount'
                            )
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.delivery_collection.vat'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >Delivery & Collection VAT</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.delivery_collection.vat
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError(
                              'figures.hire.totals.delivery_collection.vat'
                            )
                          "
                          class="help-block text-danger"
                          v-text="
                            getError(
                              'figures.hire.totals.delivery_collection.vat'
                            )
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.admin_fee.amount'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Admin Fee Amount</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.admin_fee.amount
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError('figures.hire.totals.admin_fee.amount')
                          "
                          class="help-block text-danger"
                          v-text="
                            getError('figures.hire.totals.admin_fee.amount')
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.admin_fee.vat'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Admin Fee VAT</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.admin_fee.vat
                            )
                          }}
                        </p>
                        <p
                          v-if="hasError('figures.hire.totals.admin_fee.vat')"
                          class="help-block text-danger"
                          v-text="getError('figures.hire.totals.admin_fee.vat')"
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.insurance_admin.amount'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >Insurance Admin Amount</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.insurance_admin.amount
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError(
                              'figures.hire.totals.insurance_admin.amount'
                            )
                          "
                          class="help-block text-danger"
                          v-text="
                            getError(
                              'figures.hire.totals.insurance_admin.amount'
                            )
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.insurance_admin.vat'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Insurance Admin VAT</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.insurance_admin.vat
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError('figures.hire.totals.insurance_admin.vat')
                          "
                          class="help-block text-danger"
                          v-text="
                            getError('figures.hire.totals.insurance_admin.vat')
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.high_risk_insurance.amount'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >High Risk Insurance Amount</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.high_risk_insurance
                                .amount
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError(
                              'figures.hire.totals.high_risk_insurance.amount'
                            )
                          "
                          class="help-block text-danger"
                          v-text="
                            getError(
                              'figures.hire.totals.high_risk_insurance.amount'
                            )
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.high_risk_insurance.vat'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >High Risk Insurance VAT</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.high_risk_insurance.vat
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError(
                              'figures.hire.totals.high_risk_insurance.vat'
                            )
                          "
                          class="help-block text-danger"
                          v-text="
                            getError(
                              'figures.hire.totals.high_risk_insurance.vat'
                            )
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.other.amount'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Other Amount</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.other.amount
                            )
                          }}
                        </p>
                        <p
                          v-if="hasError('figures.hire.totals.other.amount')"
                          class="help-block text-danger"
                          v-text="getError('figures.hire.totals.other.amount')"
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError('figures.hire.totals.other.vat')
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Other VAT</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(form.figures.hire.totals.other.vat)
                          }}
                        </p>
                        <p
                          v-if="hasError('figures.hire.totals.other.vat')"
                          class="help-block text-danger"
                          v-text="getError('figures.hire.totals.other.vat')"
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.claimed_amount'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label">Claimed Amount</label>
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.claimed_amount
                            )
                          }}
                        </p>
                        <p
                          v-if="hasError('figures.hire.totals.claimed_amount')"
                          class="help-block text-danger"
                          v-text="
                            getError('figures.hire.totals.claimed_amount')
                          "
                        ></p>
                      </div>
                    </div>
                    <div
                      class="form-group"
                      :class="{
                        'has-error': hasError(
                          'figures.hire.totals.claimed_amount_gross'
                        )
                      }"
                    >
                      <div class="col-lg-4">
                        <label class="control-label"
                          >Claimed Amount (Gross)</label
                        >
                      </div>
                      <div class="col-lg-8">
                        <p class="form-control-static">
                          {{
                            formatCurrency(
                              form.figures.hire.totals.claimed_amount_gross
                            )
                          }}
                        </p>
                        <p
                          v-if="
                            hasError('figures.hire.totals.claimed_amount_gross')
                          "
                          class="help-block text-danger"
                          v-text="
                            getError('figures.hire.totals.claimed_amount_gross')
                          "
                        ></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card mb-3">
                <div class="card-header">Repair</div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.repair.invoice_date')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Invoice Date</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatValue(form.figures.repair.invoice_date) }}
                          </p>
                          <p
                            v-if="hasError('figures.repair.invoice_date')"
                            class="help-block text-danger"
                            v-text="getError('figures.repair.invoice_date')"
                          ></p>
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.repair.parts_net')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Parts Net</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(form.figures.repair.parts_net) }}
                          </p>
                          <p
                            v-if="hasError('figures.repair.parts_net')"
                            class="help-block text-danger"
                            v-text="getError('figures.repair.parts_net')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.repair.labour_net')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Labour Net</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(form.figures.repair.labour_net) }}
                          </p>
                          <p
                            v-if="hasError('figures.repair.labour_net')"
                            class="help-block text-danger"
                            v-text="getError('figures.repair.labour_net')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            'figures.repair.paint_materials_net'
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label"
                            >Paint Materials Net</label
                          >
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(
                                form.figures.repair.paint_materials_net
                              )
                            }}
                          </p>
                          <p
                            v-if="
                              hasError('figures.repair.paint_materials_net')
                            "
                            class="help-block text-danger"
                            v-text="
                              getError('figures.repair.paint_materials_net')
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            'figures.repair.engineers_fee_net'
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Engineers Fee Net</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(
                                form.figures.repair.engineers_fee_net
                              )
                            }}
                          </p>
                          <p
                            v-if="hasError('figures.repair.engineers_fee_net')"
                            class="help-block text-danger"
                            v-text="
                              getError('figures.repair.engineers_fee_net')
                            "
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.repair.admin_fee_net')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Admin Fee Net</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(form.figures.repair.admin_fee_net)
                            }}
                          </p>
                          <p
                            v-if="hasError('figures.repair.admin_fee_net')"
                            class="help-block text-danger"
                            v-text="getError('figures.repair.admin_fee_net')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.repair.claimed_amount')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Claimed Amount</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(form.figures.repair.claimed_amount)
                            }}
                          </p>
                          <p
                            v-if="hasError('figures.repair.claimed_amount')"
                            class="help-block text-danger"
                            v-text="getError('figures.repair.claimed_amount')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            'figures.repair.claimed_amount_gross'
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label"
                            >Claimed Amount (Gross)</label
                          >
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(
                                form.figures.repair.claimed_amount_gross
                              )
                            }}
                          </p>
                          <p
                            v-if="
                              hasError('figures.repair.claimed_amount_gross')
                            "
                            class="help-block text-danger"
                            v-text="
                              getError('figures.repair.claimed_amount_gross')
                            "
                          ></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card mb-3">
                <div class="card-header">Recovery</div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.recovery.invoice_date')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Invoice Date</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatValue(form.figures.recovery.invoice_date)
                            }}
                          </p>
                          <p
                            v-if="hasError('figures.recovery.invoice_date')"
                            class="help-block text-danger"
                            v-text="getError('figures.recovery.invoice_date')"
                          ></p>
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.recovery.net')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Net</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(form.figures.recovery.net) }}
                          </p>
                          <p
                            v-if="hasError('figures.recovery.net')"
                            class="help-block text-danger"
                            v-text="getError('figures.recovery.net')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError('figures.recovery.vat')
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">VAT</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{ formatCurrency(form.figures.recovery.vat) }}
                          </p>
                          <p
                            v-if="hasError('figures.recovery.vat')"
                            class="help-block text-danger"
                            v-text="getError('figures.recovery.vat')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            'figures.recovery.claimed_amount'
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label">Claimed Amount</label>
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(
                                form.figures.recovery.claimed_amount
                              )
                            }}
                          </p>
                          <p
                            v-if="hasError('figures.recovery.claimed_amount')"
                            class="help-block text-danger"
                            v-text="getError('figures.recovery.claimed_amount')"
                          ></p>
                        </div>
                      </div>
                      <div
                        class="form-group"
                        :class="{
                          'has-error': hasError(
                            'figures.recovery.claimed_amount_gross'
                          )
                        }"
                      >
                        <div class="col-lg-4">
                          <label class="control-label"
                            >Claimed Amount (Gross)</label
                          >
                        </div>
                        <div class="col-lg-8">
                          <p class="form-control-static">
                            {{
                              formatCurrency(
                                form.figures.recovery.claimed_amount_gross
                              )
                            }}
                          </p>
                          <p
                            v-if="
                              hasError('figures.recovery.claimed_amount_gross')
                            "
                            class="help-block text-danger"
                            v-text="
                              getError('figures.recovery.claimed_amount_gross')
                            "
                          ></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="form.figures.recovery.inherited" class="card mb-3">
          <div class="card-header">Inherited Recovery</div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.recovery.inherited.net')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Net</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.recovery.inherited.net) }}
                    </p>
                    <p
                      v-if="hasError('figures.recovery.inherited.net')"
                      class="help-block text-danger"
                      v-text="getError('figures.recovery.inherited.net')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.recovery.inherited.vat')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">VAT</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.recovery.inherited.vat) }}
                    </p>
                    <p
                      v-if="hasError('figures.recovery.inherited.vat')"
                      class="help-block text-danger"
                      v-text="getError('figures.recovery.inherited.vat')"
                    ></p>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.recovery.inherited.claimed_amount'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claimed Amount</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(
                          form.figures.recovery.inherited.claimed_amount
                        )
                      }}
                    </p>
                    <p
                      v-if="
                        hasError('figures.recovery.inherited.claimed_amount')
                      "
                      class="help-block text-danger"
                      v-text="
                        getError('figures.recovery.inherited.claimed_amount')
                      "
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.recovery.inherited.claimed_amount_gross'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claimed Amount (Gross)</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(
                          form.figures.recovery.inherited.claimed_amount_gross
                        )
                      }}
                    </p>
                    <p
                      v-if="
                        hasError(
                          'figures.recovery.inherited.claimed_amount_gross'
                        )
                      "
                      class="help-block text-danger"
                      v-text="
                        getError(
                          'figures.recovery.inherited.claimed_amount_gross'
                        )
                      "
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.recovery.inherited.invoice_date'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Invoice Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatValue(
                          form.figures.recovery.inherited.invoice_date
                        )
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.recovery.inherited.invoice_date')"
                      class="help-block text-danger"
                      v-text="
                        getError('figures.recovery.inherited.invoice_date')
                      "
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-header">Storage</div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.start_date')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Start Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.figures.storage.start_date) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.start_date')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.start_date')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('figures.storage.end_date') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">End Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.figures.storage.end_date) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.end_date')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.end_date')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.invoice_date')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Invoice Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.figures.storage.invoice_date) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.invoice_date')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.invoice_date')"
                    ></p>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.daily_rate')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Daily Rate</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.storage.daily_rate) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.daily_rate')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.daily_rate')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{ 'has-error': hasError('figures.storage.extra') }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Extra</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.storage.extra) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.extra')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.extra')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.total_vat')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Total VAT</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.storage.total_vat) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.total_vat')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.total_vat')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.claimed_amount')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claimed Amount</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.storage.claimed_amount) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.claimed_amount')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.claimed_amount')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.storage.claimed_amount_gross'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claimed Amount (Gross)</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(
                          form.figures.storage.claimed_amount_gross
                        )
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.claimed_amount_gross')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.claimed_amount_gross')"
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="form.figures.storage.inherited" class="card mb-3">
          <div class="card-header">Inherited Storage</div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.storage.inherited.start_date'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Start Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatValue(form.figures.storage.inherited.start_date)
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.inherited.start_date')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.inherited.start_date')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.inherited.end_date')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">End Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.figures.storage.inherited.end_date) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.inherited.end_date')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.inherited.end_date')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.storage.inherited.daily_rate'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Daily Rate</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(
                          form.figures.storage.inherited.daily_rate
                        )
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.inherited.daily_rate')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.inherited.daily_rate')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.inherited.extra')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Extra</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.storage.inherited.extra) }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.inherited.extra')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.inherited.extra')"
                    ></p>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.storage.inherited.total_vat')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Total VAT</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(form.figures.storage.inherited.total_vat)
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.inherited.total_vat')"
                      class="help-block text-danger"
                      v-text="getError('figures.storage.inherited.total_vat')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.storage.inherited.claimed_amount'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claimed Amount</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(
                          form.figures.storage.inherited.claimed_amount
                        )
                      }}
                    </p>
                    <p
                      v-if="
                        hasError('figures.storage.inherited.claimed_amount')
                      "
                      class="help-block text-danger"
                      v-text="
                        getError('figures.storage.inherited.claimed_amount')
                      "
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.storage.inherited.claimed_amount_gross'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Claimed Amount (Gross)</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(
                          form.figures.storage.inherited.claimed_amount_gross
                        )
                      }}
                    </p>
                    <p
                      v-if="
                        hasError(
                          'figures.storage.inherited.claimed_amount_gross'
                        )
                      "
                      class="help-block text-danger"
                      v-text="
                        getError(
                          'figures.storage.inherited.claimed_amount_gross'
                        )
                      "
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError(
                      'figures.storage.inherited.invoice_date'
                    )
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Invoice Date</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatValue(form.figures.storage.inherited.invoice_date)
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.storage.inherited.invoice_date')"
                      class="help-block text-danger"
                      v-text="
                        getError('figures.storage.inherited.invoice_date')
                      "
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-header">Salvage</div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.salvage.pre_accident_value')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Pre-Accident Value</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{
                        formatCurrency(form.figures.salvage.pre_accident_value)
                      }}
                    </p>
                    <p
                      v-if="hasError('figures.salvage.pre_accident_value')"
                      class="help-block text-danger"
                      v-text="getError('figures.salvage.pre_accident_value')"
                    ></p>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.salvage.salvage_value')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Salvage Value</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatCurrency(form.figures.salvage.salvage_value) }}
                    </p>
                    <p
                      v-if="hasError('figures.salvage.salvage_value')"
                      class="help-block text-danger"
                      v-text="getError('figures.salvage.salvage_value')"
                    ></p>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div
                  class="form-group"
                  :class="{
                    'has-error': hasError('figures.salvage.deduct_salvage')
                  }"
                >
                  <div class="col-lg-4">
                    <label class="control-label">Deduct Salvage</label>
                  </div>
                  <div class="col-lg-8">
                    <p class="form-control-static">
                      {{ formatValue(form.figures.salvage.deduct_salvage) }}
                    </p>
                    <p
                      v-if="hasError('figures.salvage.deduct_salvage')"
                      class="help-block text-danger"
                      v-text="getError('figures.salvage.deduct_salvage')"
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="text-right">
        <button
          class="btn btn-primary"
          type="submit"
          :disabled="submitting || figuresLoading"
        >
          {{
            submitting
              ? 'Submitting...'
              : isVerifyUpdate
              ? 'Update Verify'
              : 'Send to Verify'
          }}
        </button>
      </div>
    </form>
  </div>
</template>
<script>
export default {
  props: {
    incident: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      form: new window.Form({
        claimant_vrn: '',
        claimant_name: '',
        claimant_address: '',
        claimant_postcode: '',
        claimant_insurer_id: '',
        claim_reference: '',
        claimant_insurer_claim_reference: '',
        claimant_insurer_policy_number: '',
        claimant_insurance_cover_id: '',
        claimant_vat_registered: false,
        defendant_insurer_id: '',
        defendant_insurer_policy_number: '',
        defendant_insurer_ref: '',
        defendant_vrn: '',
        defendant_name: '',
        incident_date: '',
        accident_location: '',
        accident_circumstances: '',
        garage_details: '',
        non_standard_risk_driver_reason: '',
        claim_reported_date: '',
        engineer_instructed_date: '',
        vehicle_inspected_date: '',
        report_submitted_date: '',
        repair_authorised_date: '',
        vehicle_onsite_date: '',
        repair_commenced_date: '',
        parts_ordered_date: '',
        parts_received_date: '',
        repair_completed_date: '',
        documents_requested_date: '',
        documents_received_date: '',
        offer_made_date: '',
        offer_accepted_date: '',
        payment_issued_date: '',
        payment_received_date: '',
        damage_claimed: '',
        vehicle_condition: '',
        inspection_requested_by: '',
        party_dealing: '',
        figures: null
      }),
      maxCircumstancesLength: 800,
      figuresLoading: false,
      figuresError: '',
      submitting: false,
      submitError: null,
      damageClaimedOptions: [
        { value: 'REPAIR', label: 'Repair' },
        { value: 'TOTAL_LOSS', label: 'Total Loss' },
        { value: 'CIL', label: 'CIL' }
      ],
      vehicleConditionOptions: [
        { value: 'ROADWORTHY', label: 'Roadworthy' },
        { value: 'UNROADWORTHY', label: 'Unroadworthy' }
      ],
      partyOptions: [
        { value: 'DEFENDANT', label: 'Defendant' },
        { value: 'DEFENDANT_SUPPLIER', label: 'Defendant Supplier' },
        { value: 'CLAIMANT_REPRESENTATIVE', label: 'Claimant Representative' },
        { value: 'CLAIMANT_INSURER', label: 'Claimant Insurer' },
        { value: 'DEFENDANT_AGENT', label: 'Defendant Agent' }
      ],
      dateFields: [
        { key: 'claim_reported_date', label: 'Claim Reported Date' },
        { key: 'engineer_instructed_date', label: 'Engineer Instructed Date' },
        { key: 'vehicle_inspected_date', label: 'Vehicle Inspected Date' },
        { key: 'report_submitted_date', label: 'Report Submitted Date' },
        { key: 'repair_authorised_date', label: 'Repair Authorised Date' },
        { key: 'vehicle_onsite_date', label: 'Vehicle OnSite Date' },
        { key: 'repair_commenced_date', label: 'Repair Commenced Date' },
        { key: 'parts_ordered_date', label: 'Parts Ordered Date' },
        { key: 'parts_received_date', label: 'Parts Received Date' },
        { key: 'repair_completed_date', label: 'Repair Completed Date' },
        { key: 'documents_requested_date', label: 'Documents Requested Date' },
        { key: 'documents_received_date', label: 'Documents Received Date' },
        { key: 'offer_made_date', label: 'Offer Made Date' },
        { key: 'offer_accepted_date', label: 'Offer Accepted Date' },
        { key: 'payment_issued_date', label: 'Payment Issued Date' },
        { key: 'payment_received_date', label: 'Payment Received Date' }
      ],
      dateFieldMap: {
        vehicle_inspected_date: 'inspection_date',
        repair_authorised_date: 'repairs_authorised',
        repair_commenced_date: 'repair_start_date',
        parts_ordered_date: 'date_parts_ordered',
        parts_received_date: 'date_parts_received',
        repair_completed_date: 'repair_completion_date',
        offer_made_date: 'pre_accident_value_offer_made',
        offer_accepted_date: 'pre_accident_value_offer_accepted',
        payment_received_date: 'payment_received'
      }
    };
  },

  computed: {
    client() {
      return this.$store.getters['Incident/client'];
    },

    isVerifyUpdate() {
      const incident = this.$store.getters['Incident/incident'];
      return !!(incident?.verify_incident_id || incident?.verify_reference);
    },

    verifyReference() {
      return this.$store.getters['Incident/incident']?.verify_reference || null;
    },

    verifyIncidentId() {
      return (
        this.$store.getters['Incident/incident']?.verify_incident_id || null
      );
    },

    clientVehicle() {
      return this.$store.getters['Incident/clientVehicle'];
    },

    thirdParties() {
      return this.$store.getters['Incident/thirdParties'];
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    details() {
      return this.$store.getters['Incident/details'];
    },

    repair() {
      return this.$store.getters['Incident/repair'];
    },

    dates() {
      return this.$store.getters['Incident/dates'];
    },

    insurers() {
      return this.$store.getters['Insurers/all'];
    },

    verifyClaimDetail() {
      return (
        this.$store.getters['Incident/incident']?.verify_claim_detail || null
      );
    },

    isCircumstancesEdited() {
      const original = (
        this.details?.circumstances ||
        this.details?.description ||
        ''
      ).trim();
      const current = (this.form.accident_circumstances || '').trim();

      return current !== '' && current !== original;
    },

    coverLevels() {
      return this.$store.getters['CoverLevels/all'];
    },

    insurerMap() {
      return (this.insurers || []).reduce((map, insurer) => {
        map[insurer.id] = insurer.name;
        return map;
      }, {});
    },

    coverLevelMap() {
      return (this.coverLevels || []).reduce((map, coverLevel) => {
        map[coverLevel.id] = coverLevel.label;
        return map;
      }, {});
    },

    defaultValues() {
      const thirdParty = (this.thirdParties || [])[0] || {};
      const thirdPartyInsurerId =
        thirdParty.insurer_id ||
        (thirdParty.insurer ? thirdParty.insurer.id : '');
      const claimantName = this.joinWords([
        this.valueOrEmpty(this.client?.title?.value),
        this.valueOrEmpty(this.client?.full_name)
      ]);
      const claimantAddress = this.joinWords(
        [
          this.valueOrEmpty(this.client?.address_line_one),
          this.valueOrEmpty(this.client?.address_line_two),
          this.valueOrEmpty(this.client?.city)
        ],
        ', '
      );
      const circumstances =
        this.verifyClaimDetail?.accident_circumstances ||
        this.details?.circumstances ||
        this.details?.description ||
        '';

      return {
        claimant_vrn: this.valueOrEmpty(this.clientVehicle?.registration),
        claimant_name: claimantName,
        claimant_address: claimantAddress,
        claimant_postcode: this.valueOrEmpty(this.client?.postcode),
        claim_reference: this.valueOrEmpty(this.source?.claim_reference),
        claimant_insurer_id:
          this.client?.insurance?.insurer?.id ||
          this.client?.insurance?.insurer_id ||
          '',
        claimant_insurer_claim_reference: this.valueOrEmpty(
          this.client?.insurance?.claim_number
        ),
        claimant_insurer_policy_number: this.valueOrEmpty(
          this.client?.insurance?.policy
        ),
        claimant_insurance_cover_id:
          this.client?.insurance?.cover_level?.id ||
          this.client?.insurance?.cover_level_id ||
          '',
        claimant_vat_registered: this.normalizeBoolean(
          this.client?.vat_registered
        ),
        defendant_insurer_id: thirdPartyInsurerId || '',
        defendant_insurer_policy_number: this.valueOrEmpty(
          thirdParty.policy_number
        ),
        defendant_insurer_ref: this.valueOrEmpty(thirdParty.claim_number),
        defendant_vrn: this.valueOrEmpty(thirdParty?.vehicle?.registration),
        defendant_name: this.joinWords([
          this.valueOrEmpty(thirdParty?.title?.value),
          this.valueOrEmpty(thirdParty?.full_name)
        ]),
        incident_date: this.formatDateValue(this.source?.incident_occurred_at),
        accident_location: this.valueOrEmpty(this.details?.location),
        accident_circumstances: this.truncateText(
          circumstances,
          this.maxCircumstancesLength
        ),
        garage_details:
          this.valueOrEmpty(this.repair?.company?.name) ||
          this.valueOrEmpty(this.repair?.company_name),
        claim_reported_date: this.formatDateValue(this.source?.referred_at),
        engineer_instructed_date: this.getLOUDate('engineer_instructed_date'),
        vehicle_inspected_date: this.getLOUDate('vehicle_inspected_date'),
        report_submitted_date: this.getLOUDate('report_submitted_date'),
        repair_authorised_date: this.getLOUDate('repair_authorised_date'),
        vehicle_onsite_date: this.getLOUDate('vehicle_onsite_date'),
        repair_commenced_date: this.getLOUDate('repair_commenced_date'),
        parts_ordered_date: this.getLOUDate('parts_ordered_date'),
        parts_received_date: this.getLOUDate('parts_received_date'),
        repair_completed_date: this.getLOUDate('repair_completed_date'),
        documents_requested_date: this.getLOUDate('documents_requested_date'),
        documents_received_date: this.getLOUDate('documents_received_date'),
        offer_made_date: this.getLOUDate('offer_made_date'),
        offer_accepted_date: this.getLOUDate('offer_accepted_date'),
        payment_issued_date: this.getLOUDate('payment_issued_date'),
        payment_received_date: this.getLOUDate('payment_received_date'),

        // Pre-fill the manual selectlists from a persisted Verify claim, if one
        // exists for this incident. applyDefaults() only writes when the form
        // field is currently empty, so user edits are preserved.
        damage_claimed: this.valueOrEmpty(
          this.verifyClaimDetail?.damage_claimed
        ),
        vehicle_condition: this.valueOrEmpty(
          this.verifyClaimDetail?.vehicle_condition
        ),
        inspection_requested_by: this.valueOrEmpty(
          this.verifyClaimDetail?.inspection_requested_by
        ),
        party_dealing: this.valueOrEmpty(this.verifyClaimDetail?.party_dealing)
      };
    }
  },

  watch: {
    defaultValues: {
      handler(value) {
        this.applyDefaults(value);
      },
      deep: true,
      immediate: true
    },
    incident: {
      handler() {
        this.loadFigures();
      },
      immediate: true
    },
    // When invoicing damage (repairs), Verify requires the claim to be
    // handled by the Claimant Representative — both Inspection Requested By
    // and Party Dealing must be set to CLAIMANT_REPRESENTATIVE. Force these
    // values whenever damage_claimed is REPAIR.
    'form.damage_claimed': {
      handler(value) {
        if (value === 'REPAIR') {
          this.form.inspection_requested_by = 'CLAIMANT_REPRESENTATIVE';
          this.form.party_dealing = 'CLAIMANT_REPRESENTATIVE';
        }
      },
      immediate: true
    }
  },

  methods: {
    loadFigures() {
      if (!this.incident) {
        this.$set(this.form, 'figures', null);
        return;
      }
      this.figuresLoading = true;
      this.figuresError = '';
      window.Ajax.get(`/incidents/${this.incident}/verify/figures`)
        .then(response => {
          this.$set(this.form, 'figures', this.buildFiguresForm(response.data));
        })
        .catch(() => {
          this.figuresError = 'Unable to load figures.';
        })
        .finally(() => {
          this.figuresLoading = false;
        });
    },

    submitToVerify() {
      if (!this.incident || this.submitting) {
        return;
      }

      this.submitError = null;
      this.form.errors.clear();

      this.submitting = true;
      const payload = this.form.data();

      if (!this.isEmptyValue(payload.claim_reference)) {
        payload.claim_reference = String(payload.claim_reference);
      }

      window.Ajax.post(`/incidents/${this.incident}/verify`, payload)
        .then(response => {
          if (response?.data?.incident) {
            this.$store.commit('Incident/setIncident', response.data.incident);
          }
          window.toastify.success(
            'Claim submitted to Verify successfully!',
            'Success'
          );
        })
        .catch(error => {
          const data = error?.response?.data || error;
          if (data?.errors) {
            this.form.errors.record(data.errors);
            window.toastify.error(
              'Error',
              'Oops! A validation error occurred while sending the claim to Verify'
            );
            this.scrollToFirstError();
            return;
          }
          const errorMessage = data?.error
            ? data.error
            : 'Oops! an error occurred while submitting the claim to Verify';
          this.submitError = errorMessage;
          window.toastify.error('Error', errorMessage);
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    applyDefaults(values) {
      Object.keys(values).forEach(key => {
        if (this.isEmptyValue(this.form[key])) {
          this.form[key] = values[key];
        }
      });
      this.handleCircumstancesLimit();
      this.form.claimant_vat_registered = this.normalizeBoolean(
        this.form.claimant_vat_registered
      );
    },

    handleCircumstancesLimit() {
      this.form.accident_circumstances = this.truncateText(
        this.form.accident_circumstances,
        this.maxCircumstancesLength
      );
    },

    getLOUDate(verifyDate) {
      const louDateField = this.dateFieldMap[verifyDate];

      if (!louDateField) {
        return '';
      }

      const date = this.dates.find(d => d.date.name === louDateField);

      return this.formatDateValue(date?.value || '');
    },

    formatDateValue(value) {
      if (!value) {
        return '';
      }
      const parsed = window.DateManager.parse(value);
      if (!parsed) {
        return '';
      }
      return window.DateManager.format(parsed, 'DD/MM/YYYY');
    },

    normalizeBoolean(value) {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'string') {
        const normalized = value.toLowerCase();
        if (normalized === 'true') {
          return true;
        }
        if (normalized === 'false') {
          return false;
        }
      }
      return Boolean(Number(value));
    },

    joinWords(values, separator = ' ') {
      return values.filter(value => value).join(separator);
    },

    valueOrEmpty(value) {
      return value ? value : '';
    },

    truncateText(value, limit) {
      if (!value || value.length <= limit) {
        return value || '';
      }
      let truncated = value.slice(0, limit);
      const lastSpaceIndex = truncated.lastIndexOf(' ');
      if (lastSpaceIndex > 0) {
        truncated = truncated.slice(0, lastSpaceIndex);
      }
      return `${truncated} ...`;
    },

    isEmptyValue(value) {
      return value === null || value === undefined || value === '';
    },

    formatValue(value) {
      if (typeof value === 'boolean') {
        return this.formatBoolean(value);
      }
      return this.isEmptyValue(value) ? '-' : value;
    },

    formatCurrency(value) {
      if (this.isEmptyValue(value)) {
        return '-';
      }
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return value;
      }
      return `\u00a3${numberValue.toFixed(2)}`;
    },

    formatBoolean(value) {
      if (this.isEmptyValue(value)) {
        return '-';
      }
      return value ? 'Yes' : 'No';
    },

    formatRiskReason(value) {
      if (this.isEmptyValue(value)) {
        return '-';
      }
      const normalized = value.toString().toLowerCase();
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    },

    hasError(key) {
      return this.form?.errors?.has ? this.form.errors.has(key) : false;
    },

    getError(key) {
      return this.form?.errors?.get ? this.form.errors.get(key) : '';
    },

    hireItemErrorKey(index, field) {
      return `figures.hire.items.${index}.${field}`;
    },

    hireExtraErrorKey(index, field) {
      return `figures.hire.items.${index}.daily_extras.${field}`;
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const target = this.$el.querySelector('.form-group.has-error');
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    getInsurerName(id) {
      return this.insurerMap[id] || '';
    },

    getCoverLevelName(id) {
      return this.coverLevelMap[id] || '';
    },

    buildFiguresForm(figures) {
      const hireItems = (figures?.hire?.items || []).map(item => ({
        start_date: item?.start_date ?? '',
        end_date: item?.end_date ?? '',
        vrn: item?.vrn ?? '',
        days_claimed: item?.days_claimed ?? null,
        daily_rate: item?.daily_rate ?? null,
        daily_extras: {
          automatic: item?.daily_extras?.automatic ?? null,
          car_seat: item?.daily_extras?.car_seat ?? null,
          dual_control: item?.daily_extras?.dual_control ?? null,
          estate: item?.daily_extras?.estate ?? null,
          roof_rack: item?.daily_extras?.roof_rack ?? null,
          sat_nav: item?.daily_extras?.sat_nav ?? null,
          tow_bar: item?.daily_extras?.tow_bar ?? null,
          cdw: item?.daily_extras?.cdw ?? null,
          additional_driver: item?.daily_extras?.additional_driver ?? null
        }
      }));

      return {
        hire: {
          items: hireItems,
          non_standard_risk_reason:
            figures?.hire?.non_standard_risk_reason ?? null,
          totals: {
            delivery_collection: {
              amount:
                figures?.hire?.totals?.delivery_collection?.amount ?? null,
              vat: figures?.hire?.totals?.delivery_collection?.vat ?? null
            },
            admin_fee: {
              amount: figures?.hire?.totals?.admin_fee?.amount ?? null,
              vat: figures?.hire?.totals?.admin_fee?.vat ?? null
            },
            insurance_admin: {
              amount: figures?.hire?.totals?.insurance_admin?.amount ?? null,
              vat: figures?.hire?.totals?.insurance_admin?.vat ?? null
            },
            high_risk_insurance: {
              amount:
                figures?.hire?.totals?.high_risk_insurance?.amount ?? null,
              vat: figures?.hire?.totals?.high_risk_insurance?.vat ?? null
            },
            other: {
              amount: figures?.hire?.totals?.other?.amount ?? null,
              vat: figures?.hire?.totals?.other?.vat ?? null
            },
            claimed_amount: figures?.hire?.totals?.claimed_amount ?? null,
            claimed_amount_gross:
              figures?.hire?.totals?.claimed_amount_gross ?? null,
            invoice_date: figures?.hire?.totals?.invoice_date ?? ''
          }
        },
        repair: {
          parts_net: figures?.repair?.parts_net ?? null,
          labour_net: figures?.repair?.labour_net ?? null,
          paint_materials_net: figures?.repair?.paint_materials_net ?? null,
          engineers_fee_net: figures?.repair?.engineers_fee_net ?? null,
          admin_fee_net: figures?.repair?.admin_fee_net ?? null,
          claimed_amount: figures?.repair?.claimed_amount ?? null,
          claimed_amount_gross: figures?.repair?.claimed_amount_gross ?? null,
          invoice_date: figures?.repair?.invoice_date ?? ''
        },
        recovery: {
          net: figures?.recovery?.net ?? null,
          vat: figures?.recovery?.vat ?? null,
          claimed_amount: figures?.recovery?.claimed_amount ?? null,
          claimed_amount_gross: figures?.recovery?.claimed_amount_gross ?? null,
          invoice_date: figures?.recovery?.invoice_date ?? '',
          inherited: figures?.recovery?.inherited
            ? {
                net: figures?.recovery?.inherited?.net ?? null,
                vat: figures?.recovery?.inherited?.vat ?? null,
                claimed_amount:
                  figures?.recovery?.inherited?.claimed_amount ?? null,
                claimed_amount_gross:
                  figures?.recovery?.inherited?.claimed_amount_gross ?? null,
                invoice_date: figures?.recovery?.inherited?.invoice_date ?? ''
              }
            : null
        },
        storage: {
          start_date: figures?.storage?.start_date ?? '',
          end_date: figures?.storage?.end_date ?? '',
          daily_rate: figures?.storage?.daily_rate ?? null,
          extra: figures?.storage?.extra ?? null,
          total_vat: figures?.storage?.total_vat ?? null,
          claimed_amount: figures?.storage?.claimed_amount ?? null,
          claimed_amount_gross: figures?.storage?.claimed_amount_gross ?? null,
          invoice_date: figures?.storage?.invoice_date ?? '',
          inherited: figures?.storage?.inherited
            ? {
                start_date: figures?.storage?.inherited?.start_date ?? '',
                end_date: figures?.storage?.inherited?.end_date ?? '',
                daily_rate: figures?.storage?.inherited?.daily_rate ?? null,
                extra: figures?.storage?.inherited?.extra ?? null,
                total_vat: figures?.storage?.inherited?.total_vat ?? null,
                claimed_amount:
                  figures?.storage?.inherited?.claimed_amount ?? null,
                claimed_amount_gross:
                  figures?.storage?.inherited?.claimed_amount_gross ?? null,
                invoice_date: figures?.storage?.inherited?.invoice_date ?? ''
              }
            : null
        },
        salvage: {
          pre_accident_value: figures?.salvage?.pre_accident_value ?? null,
          salvage_value: figures?.salvage?.salvage_value ?? null,
          deduct_salvage: figures?.salvage?.deduct_salvage ?? null
        }
      };
    }
  }
};
</script>
<style scoped>
.form-control-static {
  margin-top: 10px;
  margin-bottom: 0.5rem;
}
</style>
